import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Loader2 } from "lucide-react";

export default function PortalActivate() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return toast.error("Password must be at least 6 characters.");
    if (pw !== pw2) return toast.error("Passwords do not match.");
    setBusy(true);
    try {
      const { data } = await api.post("/portal/activate", { token, password: pw });
      localStorage.setItem("simo_token", data.token);
      toast.success("Account activated");
      const role = data.user?.role;
      nav(["parent", "student"].includes(role) ? "/portal" : role === "applicant" ? "/centre" : "/admin");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <PortalShell>
      <div className="w-full max-w-md" data-testid="portal-activate">
        <div className="flex items-center gap-2 text-heraldic mb-2"><ShieldCheck className="h-5 w-5" /><span className="text-xs uppercase tracking-widest">Secure activation</span></div>
        <h1 className="font-serif text-3xl text-paper">Activate your Parent Portal</h1>
        <p className="text-paper/50 text-sm mt-2">Set a password for your existing SIMO account. Your email stays the same — no new account is created.</p>
        {!token && <p className="text-amber-400 text-sm mt-4">This activation link is missing its token. Please use the button in your email.</p>}
        <form onSubmit={submit} className="mt-6 space-y-3">
          <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password" data-testid="activate-pw" className={FLD} />
          <input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} placeholder="Confirm password" data-testid="activate-pw2" className={FLD} />
          <button disabled={busy || !token} data-testid="activate-submit" className="w-full bg-heraldic text-white rounded-sm py-3 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Activate & continue</button>
        </form>
        <p className="text-paper/40 text-xs mt-4">Already activated? <Link to="/portal/login" className="text-heraldic underline">Log in</Link></p>
      </div>
    </PortalShell>
  );
}

export const FLD = "w-full bg-white/5 border border-white/10 rounded-sm px-3 py-3 text-sm text-paper placeholder:text-paper/30 focus:border-heraldic/60 outline-none";

export function PortalShell({ children }) {
  return (
    <div className="min-h-screen bg-[#0a0b0f] flex">
      <div className="hidden md:flex flex-col justify-between w-1/2 bg-[#0E1017] p-12 border-r border-white/5">
        <Link to="/" className="font-serif text-xl text-paper tracking-wide">SIMO <span className="text-heraldic">UNIVERSITY</span></Link>
        <div>
          <h2 className="font-serif text-4xl text-paper leading-tight">The family home for your child's intellectual trajectory.</h2>
          <p className="text-paper/40 mt-4 text-sm max-w-sm">Read approved academic reports online, follow development over time, and keep every report in one secure place.</p>
        </div>
        <div className="text-paper/30 text-xs">Lux Simonis Populis</div>
      </div>
      <div className="flex-1 flex items-center justify-center p-8">{children}</div>
    </div>
  );
}
